import { Check, FileText, GitBranch, Scale, Brain, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface GlobalPhaseTimelineProps {
  currentPhase: 1 | 2 | 3 | 4; // 4 = Report
  sessionId: string;
  onPhaseClick?: (phase: number) => void;
  /** Completed phase gates - prevents skipping. If not provided, only backward navigation is allowed. */
  completedPhases?: number[];
}

const PHASES = [
  { 
    phase: 1, 
    label: "Analyse", 
    shortLabel: "1", 
    icon: Brain,
    description: "Problemkern verstehen"
  },
  { 
    phase: 2, 
    label: "Optionen", 
    shortLabel: "2", 
    icon: GitBranch,
    description: "Strategische Optionen entwickeln"
  },
  { 
    phase: 3, 
    label: "Bewertung", 
    shortLabel: "3", 
    icon: Scale,
    description: "Optionen bewerten"
  },
  { 
    phase: 4, 
    label: "Report", 
    shortLabel: "R", 
    icon: FileText,
    description: "Handlungsempfehlungen"
  },
];

export const GlobalPhaseTimeline = ({
  currentPhase,
  sessionId,
  onPhaseClick,
  completedPhases = [],
}: GlobalPhaseTimelineProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentIndex = PHASES.findIndex(p => p.phase === currentPhase);
  const currentPhaseData = PHASES[currentIndex];
  const CurrentIcon = currentPhaseData.icon;

  const handlePhaseClick = (phase: number, index: number) => {
    if (!onPhaseClick) return;
    // Only allow navigation to completed phases (going back) or forward if gate is passed
    if (index < currentIndex) {
      onPhaseClick(phase);
      setIsOpen(false);
    } else if (index > currentIndex && completedPhases.includes(phase - 1)) {
      // Forward navigation only if the previous phase is marked complete
      onPhaseClick(phase);
      setIsOpen(false);
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button className="flex items-center gap-3 px-4 py-2 rounded-xl bg-card border border-border hover:border-primary/30 hover:bg-accent/50 transition-colors group">
            {/* Current phase indicator */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <CurrentIcon className="w-4 h-4 text-primary" />
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-xs text-muted-foreground">Phase {currentIndex + 1}/4</p>
                <p className="text-sm font-medium">{currentPhaseData.label}</p>
              </div>
            </div>
            
            {/* Progress dots */}
            <div className="flex items-center gap-1.5">
              {PHASES.map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    "w-2 h-2 rounded-full transition-colors",
                    index < currentIndex && "bg-primary",
                    index === currentIndex && "bg-primary ring-2 ring-primary/20",
                    index > currentIndex && "bg-border"
                  )}
                />
              ))}
            </div>
            
            <ChevronDown className={cn(
              "w-4 h-4 text-muted-foreground transition-transform",
              isOpen && "rotate-180"
            )} />
          </button>
        </PopoverTrigger>
        
        <PopoverContent 
          align="start" 
          className="w-80 p-3"
          sideOffset={8}
        >
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground px-2 pb-2">
              Phasen-Übersicht
            </p>
            
            {PHASES.map((phaseItem, index) => {
              const isCompleted = index < currentIndex;
              const isCurrent = index === currentIndex;
              const isFuture = index > currentIndex;
              const isForwardReachable = isFuture && completedPhases.includes(phaseItem.phase - 1);
              const isClickable = (isCompleted || isForwardReachable) && !!onPhaseClick;
              const Icon = phaseItem.icon;

              return (
                <Tooltip key={phaseItem.phase}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => handlePhaseClick(phaseItem.phase, index)}
                      disabled={!isClickable}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors text-left",
                        isClickable && "hover:bg-accent cursor-pointer",
                        isCurrent && "bg-primary/5 border border-primary/20",
                        isFuture && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      {/* Phase icon */}
                      <div className={cn(
                        "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
                        isCurrent && "bg-primary text-primary-foreground",
                        isCompleted && "bg-primary/10",
                        isFuture && "bg-muted"
                      )}>
                        {isCompleted ? (
                          <Check className="w-4 h-4 text-primary" />
                        ) : (
                          <Icon className={cn(
                            "w-4 h-4",
                            isCurrent && "text-primary-foreground",
                            isFuture && "text-muted-foreground"
                          )} />
                        )}
                      </div>
                      
                      {/* Phase info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            "font-medium text-sm",
                            isCurrent && "text-primary",
                            isFuture && "text-muted-foreground"
                          )}>
                            {phaseItem.label}
                          </span>
                          {isCurrent && (
                            <span className="text-[10px] font-medium bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                              Aktiv
                            </span>
                          )}
                          {isCompleted && (
                            <span className="text-[10px] font-medium bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                              ✓
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {phaseItem.description}
                        </p>
                      </div>
                      
                      {/* Phase number */}
                      <span className="text-xs text-muted-foreground font-mono">
                        {index + 1}
                      </span>
                    </button>
                  </TooltipTrigger>
                  {isCompleted && (
                    <TooltipContent side="right" className="text-xs">
                      Klicken um zurückzukehren
                    </TooltipContent>
                  )}
                  {isFuture && (
                    <TooltipContent side="right" className="text-xs">
                      Noch nicht freigeschaltet
                    </TooltipContent>
                  )}
                </Tooltip>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
};
